import React from "react";
import { useTranslation } from "react-i18next";
import { UserOptions as UserOptionsAPI } from "../chat/UserOptions";
import { Link } from "react-router-dom";
import LandingIntro from "../LandingIntro";
import InputText from "../InputText";
import LanguageSelector from "../commons/LanguageSelector";
import SingleLanguageSelector from "../commons/SingleLanguageSelector";
import ErrorText from "./ErrorText";
import { createUser } from "./ApiService";
import useForm from "./UseForm";
import { PiSignIn } from "react-icons/pi";

interface FormData {
  name: string;
  password: string;
  emailId: string;
  selectedNativeLanguage: string;
  selectedTargetLanguage: string;
  selectedExplanationLanguage: string;
}

function Register() {
  const { t } = useTranslation();
  const { formData, errorMessage, loading, updateFormValue, setErrorMessage, setLoading } = useForm<FormData>({
    name: "",
    password: "",
    emailId: "",
    selectedNativeLanguage: "",
    selectedTargetLanguage: "",
    selectedExplanationLanguage: "",
  });

  const submitForm = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrorMessage("");
    try {
      setLoading(true);
      const response: any = await createUser({
        first_name: formData.name.trim(),
        email: formData.emailId.trim(),
        password: formData.password.trim(),
        native_language_iso639: formData.selectedNativeLanguage,
        target_language_iso639: formData.selectedTargetLanguage,
        preferred_explanation_language_iso639: formData.selectedExplanationLanguage || 'en-US',
      });
      localStorage.setItem("accessToken", response.access);
      localStorage.setItem("refreshToken", response.refresh);
      // Fetch and store userOptions after signup
      try {
        const userOptions = await UserOptionsAPI();
        localStorage.setItem("userOptions", JSON.stringify(userOptions));
      } catch (err) {
        console.error("Failed to fetch user options after signup", err);
      }
      setLoading(false);
      window.location.hash = "/chat";
    } catch (error) {
      console.error("There was a problem with the fetch operation:", error);
      setErrorMessage(t('auth.errorSignup'));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center">
      <div className="card mx-auto w-full max-w-5xl shadow-xl">
        <div className="grid md:grid-cols-2 grid-cols-1 bg-base-300 text-base-content rounded-xl">
          <div className="py-8 px-10">
            <h2 className="text-2xl font-semibold mb-2 text-center">{t('auth.tryGPTandem')}</h2>
            <p className="py-2">{t('auth.signupDescription')}</p>
            <form onSubmit={(e) => submitForm(e)}>
              <div className="mb-4">
                <InputText
                  defaultValue={formData.name}
                  updateType="name"
                  containerStyle="mt-1"
                  labelTitle={t('auth.firstName')}
                  updateFormValue={updateFormValue}
                />
                <LanguageSelector
                  selectedNativeLanguage={formData.selectedNativeLanguage}
                  selectedTargetLanguage={formData.selectedTargetLanguage}
                  updateFormValue={updateFormValue}
                />
                <SingleLanguageSelector
                  label={t('auth.explanationLanguage')}
                  selectedValue={formData.selectedExplanationLanguage}
                  updateType="selectedExplanationLanguage"
                  updateFormValue={updateFormValue}
                  languages={[
                    { language_name: t('languages.english'), iso639_tag: 'en-US' },
                    { language_name: t('languages.spanish'), iso639_tag: 'es-CL' },
                    { language_name: t('languages.german'), iso639_tag: 'de-DE' }
                  ]}
                  disabled={!formData.selectedNativeLanguage}
                />
                <InputText
                  defaultValue={formData.emailId}
                  updateType="emailId"
                  containerStyle="mt-1"
                  labelTitle={t('auth.email')}
                  updateFormValue={updateFormValue}
                />
                <InputText
                  defaultValue={formData.password}
                  type="password"
                  updateType="password"
                  containerStyle="mt-1"
                  labelTitle={t('auth.password')}
                  updateFormValue={updateFormValue}
                />
              </div>
              <ErrorText styleClass="mt-8">{errorMessage}</ErrorText>
              <button type="submit" className={"btn mt-2 w-full btn-primary" + (loading ? " loading" : "")}>
                {t('auth.signupButton')}
              </button>
              <div className="text-center mt-4">
                {t('auth.loginPrompt')}{" "}
                <Link to="/">
                  <span className="link inline hover:text-primary hover:underline hover:cursor-pointer transition duration-200">
                    {t('auth.loginLink')} <PiSignIn className="inline" />
                  </span>
                </Link>
              </div>
              <h2 className="py-4">{t('app.underDevelopment')}</h2>
            </form>
          </div>
          <div className="">
            <LandingIntro />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register;
